import { CalculatePricePromotion } from '@teko/cart/v2';
import { GiftFilled } from '@teko/ds-icon';
import {
  Body,
  Caption,
  Image,
  LazyImage,
  Modal,
  Shape,
  styled,
} from '@teko/ds-mobile';
import defaultPromotion from 'assets/sgc-default-promotion.svg';
import DOMPurify from 'dompurify';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useEffect, useMemo, useRef, useState } from 'react';

interface DefaultPromotionsProps {
  promotions: CalculatePricePromotion['promotions'];
}

export const DefaultPromotions: React.FC<DefaultPromotionsProps> = (props) => {
  const { promotions } = props;
  const { epicOmni1643 } = useTenantConfig();
  const [previewImg, setPreviewImg] = useState<string>('');
  const promotionListRef = useRef<HTMLUListElement>(null);

  // Intercept clicks on <img> tags rendered inside dangerouslySetInnerHTML
  useEffect(() => {
    const ul = promotionListRef.current;
    if (!ul) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        const src = (target as HTMLImageElement).src;
        if (!!src) setPreviewImg(src);
      }
    };

    ul.addEventListener('click', handleClick);
    return () => ul.removeEventListener('click', handleClick);
  }, []);

  // Check if all promotions disabled (isSelected===false) or only some disabled
  const { hasPromotionSelected, hasPromotionDisabled } = useMemo(() => {
    let hasPromotionSelected = false;
    let hasPromotionDisabled = false;
    for (const promotion of promotions) {
      if (!promotion.title) continue;
      if (promotion.isSelected) {
        hasPromotionSelected = true;
      } else {
        hasPromotionDisabled = true;
      }
      if (hasPromotionSelected && hasPromotionDisabled) break;
    }
    return { hasPromotionSelected, hasPromotionDisabled };
  }, [promotions]);

  return (
    <>
      <Shape
        padding={epicOmni1643 ? 's0' : ['s8', 's0']}
        width="100%"
        backgroundColor="white"
        marginTop={epicOmni1643 ? 's12' : 's0'}
      >
        <RelativeWrapper disabled={!hasPromotionSelected}>
          {epicOmni1643 ? (
            <StyledLabel>
              <GiftFilled size={20} color="white" />
              <Caption level={1} weight="medium" color="white">
                {t('Bạn sẽ nhận được')}
              </Caption>
            </StyledLabel>
          ) : (
            <Shape paddingLeft="s8" style={{ marginBottom: -15 }}>
              <LazyImage src={defaultPromotion} width="140px" height="24px" />
            </Shape>
          )}
          <PromotionsWrapper epicOmni1643={epicOmni1643}>
            <StyledUl ref={promotionListRef}>
              {promotions.map((promotion, index) => {
                // tier promotions will have no title
                return !!promotion.title ? (
                  <li
                    key={`${index}${promotion.promotionId}`}
                    // When all promotions are disabled -> not apply single opacity
                    style={
                      !promotion.isSelected && !!hasPromotionSelected
                        ? { opacity: 0.3 }
                        : undefined
                    }
                  >
                    <Body level={1} color="textTitle">
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            DOMPurify?.sanitize?.(promotion.title) ||
                            promotion.title,
                        }}
                        style={{ whiteSpace: 'pre-line' }}
                      />
                      <Caption level={1} color="pending700">
                        {promotion.highlightedDescriptions?.[0]}
                      </Caption>
                    </Body>
                  </li>
                ) : null;
              })}
            </StyledUl>
            {epicOmni1643 && hasPromotionDisabled && (
              <Body
                level={1}
                color="pending700"
                style={{
                  fontStyle: 'italic',
                }}
              >
                {t(
                  'Khuyến mãi này không áp dụng cùng khuyến mãi đang chọn tại mục Ưu đãi đặc biệt',
                )}
              </Body>
            )}
          </PromotionsWrapper>
        </RelativeWrapper>
      </Shape>

      {previewImg && (
        <Modal
          visible={!!previewImg}
          onClose={() => setPreviewImg('')}
          width="max-content"
          disableEscClose={false}
          showCloseIcon
          footer={null}
        >
          <Image
            src={previewImg}
            width="100%"
            style={{ maxWidth: '80vw', maxHeight: '80vh' }}
          />
        </Modal>
      )}
    </>
  );
};

const RelativeWrapper = styled.div<{ disabled?: boolean }>`
  position: relative;
  ${({ disabled }) => disabled && `opacity: 0.4;`}
`;

const PromotionsWrapper = styled(Shape)<{ epicOmni1643?: boolean }>(
  ({ epicOmni1643, theme }) => `
  padding: 16px 8px;
  padding-top: 24px;
  background: ${
    !epicOmni1643
      ? 'linear-gradient(180deg,rgba(250, 225, 76, 0.1) 4.81%,rgba(250, 225, 76, 0) 100%)'
      : theme.colors.secondary100
  };
  border:  ${!epicOmni1643 ? '1px solid #ffd591' : 'none'};
  border-radius: ${!epicOmni1643 ? '8px' : '4px'};
`,
);

const StyledUl = styled.ul`
  padding-left: 0; // remove default padding of ul
  margin: 0;
  margin-left: 1.5rem;
  img {
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid #0000000f;
    object-fit: contain;
    cursor: zoom-in;
  }
`;

const StyledLabel = styled.div`
  position: absolute;
  top: -12px;
  left: 8px;
  z-index: 1;
  width: max-content;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
  padding: 2px 10px 2px 10px;
  background: ${({ theme }) => theme.colors.primary};
`;
